const DropdownBehavior = require('./DropdownBehavior');
const ExpandableBehavior = require('./ExpandableBehavior');
const {debounce} = require('./helpers/Debounce');
const TabBehavior = require('./helpers/TabBehavior');
const {CollapsibleFigures, CollapsibleText} = require('./helpers');
const scrollIndicator = require('../../lib/scrollIndicator');

class VisualComponent {
    main; // <DOMElement> element expected
    articleView = {}; // Article parent object reference is expected
    s = {
        FOCUSED: 'focused',
        HIDDEN: 'hidden',
    };
    root = document.querySelector(':root');

    constructor(articleView) {
        this.articleView = articleView;
    }

    /**
     * @param {HTMLElement} target - the Node that we want to scroll to
     * @param {object} focus - the focus related params
     * @param {boolean} [focus.enable=true] - if we also want to set focus to it
     * @param {boolean} [focus.focusout=] - attach the focusout event listener
     * @param {boolean} [focus.click=] - attach the click event listener
     * @param {boolean} [focus.keyup=] - attach the keyup event listener
     * @param {object} customScroll - enable jQuery animate with custom scrolltop
     * @param {boolean} [customScroll.enable=true] - if we want to use jQuery animate with custom scrolltop
     * @param {integer} customScroll.topCorrection - if we also want to use jQuery animate with custom scrolltop
     * @param {integer} customScroll.speed - if we also want to use jQuery animate with custom scrolltop
     * @param {object} options - if we want to customize the scrollIntoView behavior
     */
    animateTo(
        target,
        focus = {enable: true, focusout: false},
        customScroll = {enable: false, speed: 150},
        options = {behavior: 'smooth'}
    ) {
        if (target.tagName === 'FIGURE' && this.articleView?.config?.figures.focusOnWrapper) {
            target = target.closest('.figure-wrap');
        }

        const that = this;
        if (customScroll.enable) {
            $('html, body').animate(
                {scrollTop: this.getElementPosition(target).top - customScroll.topCorrection},
                customScroll.speed,
                () => {
                    focus.enable && that.focus(target, focus.focusout, focus.click, focus.keyup);
                }
            );
        } else {
            let retried = false;
            window.requestAnimationFrame(() => {
                target.scrollIntoView(options);
                window.onscrollend = () => {
                    if (
                        retried < 3 && // try at most 3 times to be pixel perfect... it's not worth it any more tries
                        that.isInViewport(target) &&
                        Math.ceil(target.getBoundingClientRect().top) !==
                            Math.ceil(parseFloat(that.cssVar_get('--article-sticky-top')))
                    ) {
                        target.scrollIntoView(options);
                        retried++;
                    }
                };
            });
            focus.enable && this.focus(target, focus.focusout, focus.click, focus.keyup);
        }
    }
    /**
     * @param {HTMLElement} target - the Node that we want to set focus to
     * @param {boolean} [focusout=false]- attach the focusout event listener
     * @param {boolean} [click=true] - attach the click event listener
     * @param {boolean} [keyup=true] - attach the keyup event listener
     */
    focus(target, focusout = false, click = true, keyup = true) {
        try {
            if (target.classList.contains('focused') || target.parentNode.classList.contains('focused')) {
                document.body.click();
            }
        } catch (error) {
            console.debug('failed to remove focus', {target, focusout, click, keyup}, error);
        }

        target.classList.add(this.s.FOCUSED);
        click &&
            document.body.addEventListener(
                'click',
                () => {
                    target.classList.remove(this.s.FOCUSED);
                },
                {once: true}
            );
        focusout &&
            document.body.addEventListener(
                'focusout',
                () => {
                    target.classList.remove(this.s.FOCUSED);
                },
                {once: true}
            );
        keyup &&
            document.body.addEventListener(
                'keyup',
                () => {
                    target.classList.remove(this.s.FOCUSED);
                },
                {once: true}
            );
        target.focus({preventScroll: true});
    }

    hide() {
        if (this.main) {
            this.main.classList.add(this.s.HIDDEN);
        }
    }
    disableAccessibly(target) {
        target.setAttribute('aria-disabled', 'true');
        target.setAttribute('disabled', 'true');
        target.setAttribute('tabindex', '-1');
    }
    enableAccessibly(target) {
        target.removeAttribute('aria-disabled', 'true');
        target.removeAttribute('disabled', 'true');
        target.getAttribute('tabindex') === '-1' && target.setAttribute('tabindex', '0');
    }
    show() {
        if (this.main) {
            this.main.classList.remove(this.s.HIDDEN);
        }
    }

    hideElement(element) {
        if (element) {
            element.setAttribute('aria-hidden', 'true');
            const interactiveElements = element.querySelectorAll('button, a, input, select, textarea');
            interactiveElements.forEach(el => {
                el.setAttribute('data-orig-tabindex', el.getAttribute('tabindex') || '0');
                el.setAttribute('tabindex', '-1');
                el.setAttribute('aria-hidden', 'true');
            });
        }
    }

    showElement(element) {
        if (element) element.setAttribute('aria-hidden', 'false');
        const interactiveElements = element.querySelectorAll('button, a, input, select, textarea');
        interactiveElements.forEach(el => {
            const origTabindex = el.getAttribute('data-orig-tabindex');
            if (origTabindex !== null) {
                el.setAttribute('tabindex', origTabindex);
            } else {
                el.removeAttribute('tabindex');
            }
            el.removeAttribute('aria-hidden');
            el.removeAttribute('data-orig-tabindex');
        });
    }

    stringToHTML(string) {
        return document.createRange().createContextualFragment(string);
    }

    initiate() {}

    addEventListeners() {}
    restrainIntoView(element, offset, secondary_el) {
        const calculateScrollPosition = () => {
            const dock = this.articleView.getArticle().nextSibling;
            if (!dock) return;
            const dockPosition = dock.offsetTop;
            const menuDimensions = element.offsetHeight + offset + 20;
            const scrollTop = document.documentElement.scrollTop;
            let marginTop = 0;
            if (scrollTop + menuDimensions > dockPosition) {
                marginTop = dockPosition - scrollTop - menuDimensions + 'px';
            }
            element.style.marginTop = marginTop;
            if (secondary_el) secondary_el.style.marginTop = marginTop;
        };
        this.debounce({func: calculateScrollPosition(), wait: 250, immediate: true});
    }

    isInDOM(elem) {
        const target = elem || this.main;
        if (!target) {
            return false;
        }
        const values = () => {
            const {top, right, bottom, left, width, height} = target.getBoundingClientRect();
            return {top, right, bottom, left, width, height};
        };
        return Object.values(values()).reduce((acc, c) => Math.abs(c) + acc, 0) !== 0;
    }
    isInViewport(el) {
        const rect = el.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    }
    getElementPosition(target) {
        const box = target.getBoundingClientRect();

        const body = document.body;
        const docEl = document.documentElement;

        const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
        const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

        const clientTop = docEl.clientTop || body.clientTop || 0;
        const clientLeft = docEl.clientLeft || body.clientLeft || 0;

        const top = box.top + scrollTop - clientTop;
        const left = box.left + scrollLeft - clientLeft;
        return {top: Math.round(top), left: Math.round(left)};
    }
    isHidden(el) {
        return el.offsetParent === null;
    }

    iOS() {
        return (
            ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
                navigator.platform
            ) ||
            // iPad on iOS 13 detection
            (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
        );
    }

    getSiblings(elem) {
        const target = elem || this.main;
        // Setup siblings array and get the first sibling
        let siblings = [];
        let sibling = target.parentNode.firstChild;

        // Loop through each sibling and push to the array
        while (sibling) {
            if (sibling.nodeType === 1 && sibling !== target) {
                siblings.push(sibling);
            }
            sibling = sibling.nextSibling;
        }
        return siblings;
    }

    /**
     * @param {HTMLElement} target - the Node that we test if it's empty
     * @param {string} extraTest - f.ex 'H3'
     */
    checkIfEmpty(target, extraTest = undefined) {
        /*
         if the pane contains only a comment (or the extra-tested-element),
         it's considered empty, and we should not proceed with any other calculations
         */
        const contents = [...target.childNodes].filter(child => {
            const tag = child.tagName ? child.tagName : child.nodeName;
            if (extraTest) {
                return tag !== extraTest && tag !== '#comment';
            } else {
                return tag !== '#comment';
            }
        });
        target.notAvailable = contents.length === 0;
        return contents.length === 0;
    }

    /**
     * @param {object} params
     * @param {void} params.func - The function to be debounced.
     * @param {number} params.wait - the waiting ms
     * @param {boolean} params.immediate - if we want to run it immediately
     * @param {function()} callback - The callback function.
     */
    debounce(params, callback = undefined) {
        debounce(params.func, params.wait, params.immediate);
        callback && debounce(callback(), params.wait);
    }

    /**
     * @param {Object} options - The selector for the wrapper element
     * @param {string} [options.wrapper] - The selector for the wrapper element
     * @param {string} [options.selector] - the selector for the trigger buttons
     * @param {boolean} [options.closeOnOutsideClick] - if we want to auto-close the dropdown on clicking outside
     * @param {function(*)*} [options.callback] - The callback function of the trigger buttons.
     * @param {boolean} [options.focusNext] - if we want to focus on the newly opened element
     */
    setDropdownBehavior(options) {
        try {
            new DropdownBehavior(
                options.wrapper,
                options.selector,
                options.closeOnOutsideClick,
                options.callback,
                options.focusNext
            );
        } catch (error) {
            console.debug('Dropdown behavior failed for', {options}, error);
        }
    }
    /**
     * @param {{classes: {CHEVRON: (string|*), COLLAPSED_ICON: (string|*), EXPANDED_ICON: (string|*)}, selectors: (*&{LIST: *, TOGGLE_ALL_WRAPPER: *}), settings: (*&{HEADING_LEVEL: (string)})}} params
     */
    setExpandableBehavior(params) {
        try {
            new ExpandableBehavior(params);
        } catch (error) {
            console.debug('Expandable behavior failed for', params, error);
        }
    }
    /**
     * @param {HTMLElement} target - The wrapper element containing the elements to be converted to the tabs component
     * @param {object} config - The configuration object.
     */
    setTabBehavior(target, config) {
        try {
            new TabBehavior(target, config);
        } catch (error) {
            console.debug('Tab behavior failed for', {target, config}, error);
        }
    }
    /**
     @param {object} config - The configuration object.
     @param {string} [config.method] - whether the collapsing should happen on specific element number or height in pixels
     @param {boolean} [config.keepSession] - whether the collapsing state should be remembered between page loads
     @param {string} config.wrapper - the container that wraps the to-be-collapsed elements
     @param {string} config.templateId the id of the template to be used in order to create the toggling buttons
     @param {number} config.value the id of the template to be used in order to create the toggling buttons
     @param {string} [config.parent] the Node where we should confine our querySelectors, defaults to document.body
     @param {string} [config.item] the selector for the items to be collapsed
     @param {string} [config.key] - OPTIONAL - the key for the object
     */
    setCollapsibleText(config, key) {
        try {
            const configWithKey = {...config, templateId: key + '_truncate_template'};
            new CollapsibleText(configWithKey);
        } catch (error) {
            console.debug('Collapsible Text behavior failed for', {config}, error);
        }
    }
    /**
     * @param {object} data - The data object.
     * @param {object} config - The configuration object.
     */
    setCollapsibleFigures(data, config) {
        const splicedData = [...data];
        try {
            splicedData.forEach((figure, index) => {
                !!figure.multiFigure && splicedData.splice(index, 1, ...figure.multiFigure);
            });
            new CollapsibleFigures(splicedData, config);
        } catch (error) {
            console.debug('Collapsible Figure behavior failed for', {data, splicedData, config}, error);
        }
    }

    // manipulate CSS variables
    cssVar_get(variable) {
        const rs = getComputedStyle(this.root);
        return rs.getPropertyValue(variable);
    }
    cssVar_set(variable, newValue) {
        this.root.style.setProperty(variable, newValue);
    }
    setScrollIndicator(enabled, element) {
        if (enabled && element) {
            scrollIndicator?.initFor(element);
        }
    }

    /**
     * Recursively deep merges two objects, with properties from the source object
     * taking precedence over those in the target object.
     *
     * @param {Object} target - The target object to merge into.
     * @param {Object} source - The source object whose properties override target.
     * @returns {Object} - The merged object with combined properties.
     *
     * @example
     * const obj1 = { a: 1, b: { c: 2 } };
     * const obj2 = { b: { d: 3 }, e: 4 };
     * const result = deepMerge(obj1, obj2);
     * console.log(result); // { a: 1, b: { c: 2, d: 3 }, e: 4 }
     */
    deepMerge(target, source) {
        target = structuredClone(target);
        if (typeof target !== 'object' || typeof source !== 'object') {
            return source;
        }

        for (const key in source) {
            if (source.hasOwnProperty(key)) {
                if (typeof source[key] === 'object' && !Array.isArray(source[key])) {
                    target[key] = this.deepMerge(target[key] || {}, source[key]);
                } else {
                    target[key] = source[key]; // Override primitive values or arrays
                }
            }
        }
        return target;
    }
}

module.exports = VisualComponent;
